import { api } from '../api/client';
import { API_ENDPOINTS } from '../api/endpoints';
import { TokenStorage } from './tokenStorage';
import { TokenService } from './tokenService';
import type { TokenData } from './tokenTypes';

// Authentication types
export interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
  name: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponseWithExpiry extends AuthResponse {
  accessTokenExp: number;
  refreshTokenExp: number;
}

export interface MpnAuthResponse {
  accessToken: string;
  accessTokenExp?: number;
  refreshToken?: string;
  refreshTokenExp?: number;
  user?: User;
}

export const authService = {
  /**
   * Exchange Microsoft ID token for MPN access token
   * @param idToken - The Microsoft ID token from MSAL
   * @returns MPN access token and optional user info
   */
  async exchangeMicrosoftToken(idToken: string): Promise<MpnAuthResponse> {
    const response = await api.post<MpnAuthResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      { idToken },
      { skipAuth: true }
    );

    // Handle both wrapped and unwrapped responses
    // If response.data has a 'data' property, it's wrapped in ApiResponse
    // Otherwise, response.data is the MpnAuthResponse directly
    type ResponseWrapper = { data?: MpnAuthResponse };
    const authResponse =
      (response.data as ResponseWrapper).data || response.data;

    const result = authResponse as MpnAuthResponse;

    // Store tokens with the new TokenStorage system if expiration times are available
    if (
      result.accessToken &&
      result.accessTokenExp &&
      result.refreshToken &&
      result.refreshTokenExp
    ) {
      const tokenData: TokenData = {
        accessToken: result.accessToken,
        accessTokenExp: result.accessTokenExp,
        refreshToken: result.refreshToken,
        refreshTokenExp: result.refreshTokenExp,
      };
      TokenStorage.setTokens(tokenData);
    }

    return result;
  },
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials,
      { skipAuth: true }
    );

    // Handle both wrapped and unwrapped responses
    type ResponseWrapper = { data?: AuthResponse };
    const authResponse =
      (response.data as ResponseWrapper).data || response.data;

    return authResponse as AuthResponse;
  },

  /**
   * Login with token expiry data
   * Stores tokens with expiration times for the new token system
   */
  async loginWithExpiry(
    credentials: LoginCredentials
  ): Promise<AuthResponseWithExpiry> {
    const response = await api.post<AuthResponseWithExpiry>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials,
      { skipAuth: true }
    );

    const authData = response.data.data;

    // Store tokens with expiry if available
    if (
      authData.accessTokenExp &&
      authData.refreshTokenExp &&
      authData.accessToken &&
      authData.refreshToken
    ) {
      const tokenData: TokenData = {
        accessToken: authData.accessToken,
        accessTokenExp: authData.accessTokenExp,
        refreshToken: authData.refreshToken,
        refreshTokenExp: authData.refreshTokenExp,
      };
      TokenStorage.setTokens(tokenData);
    }

    return authData;
  },

  /**
   * Store token data after successful authentication
   */
  storeTokens(tokenData: TokenData): void {
    TokenStorage.setTokens(tokenData);
  },

  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      credentials,
      { skipAuth: true }
    );

    // Handle both wrapped and unwrapped responses
    type ResponseWrapper = { data?: AuthResponse };
    const authResponse =
      (response.data as ResponseWrapper).data || response.data;

    return authResponse as AuthResponse;
  },

  /**
   * Call backend logout API to revoke tokens
   * @param accessToken - The access token to include in Authorization header
   */
  async logout(accessToken?: string): Promise<void> {
    try {
      // Try to get token from new storage first
      const token = accessToken || TokenStorage.getAccessToken();

      if (token) {
        await api.post(API_ENDPOINTS.AUTH.LOGOUT, undefined, {
          skipAuth: true,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      // Even if logout fails on server, we should continue with local cleanup
      console.warn('Logout request failed:', error);
    } finally {
      // Clear all tokens
      TokenService.clearTokens();
    }
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>(API_ENDPOINTS.AUTH.ME);

    // Handle both wrapped and unwrapped responses
    type ResponseWrapper = { data?: User };
    const user = (response.data as ResponseWrapper).data || response.data;

    return user as User;
  },

  /**
   * Refresh access token using the new token refresh endpoint
   * @returns New token data with expiration times
   */
  async refreshToken(): Promise<TokenData> {
    const newAccessToken = await TokenService.refreshAccessToken();

    if (!newAccessToken) {
      throw new Error('Failed to refresh token');
    }

    const tokens = TokenStorage.getTokens();
    if (!tokens) {
      throw new Error('No tokens available after refresh');
    }

    return {
      accessToken: tokens.accessToken,
      accessTokenExp: tokens.accessTokenExp,
      refreshToken: tokens.refreshToken,
      refreshTokenExp: tokens.refreshTokenExp,
    };
  },

  /**
   * Check if user is authenticated
   * Checks both new token storage and MSAL
   */
  isAuthenticated(): boolean {
    // Check new token system first
    const hasValidToken = !!TokenStorage.getAccessToken();
    if (hasValidToken) {
      return true;
    }

    // Fallback to legacy MPN token check
    return !!localStorage.getItem('mpn_access_token');
  },

  getStoredUser(): User | null {
    try {
      const storedUser = localStorage.getItem('auth-storage');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        return parsed.state?.user || null;
      }
    } catch (error) {
      console.warn('Failed to parse stored user:', error);
    }
    return null;
  },
};
